export interface BackfillContact {
	personId: number | null;
	clientId: number | null;
	clientName: string | null;
	firstName: string | null;
	lastName: string | null;
	email: string | null;
	personCreatedAt: string | null;
	reason: string | null;
	willCreate: boolean | null;
}

export interface BackfillCompany {
	companyId: number | null;
	companyName: string | null;
	tier: string | null;
	contactsToCreate: number | null;
	contactsSkipped: number | null;
	contacts: ReadonlyArray<BackfillContact | null> | null;
}

export interface BackfillFailure {
	companyId: number | null;
	companyName: string | null;
	tier: string | null;
	error: string | null;
}

/** Plain-language label for each reason a real run creates a contact. */
export const REASON_LABELS: Record<string, string> = {
	NO_CONTACT_ROW: 'No contact record',
	CONTACT_DELETED: 'Contact was deleted',
	CONTACT_ON_OTHER_CLIENT: 'Contact is on another client',
};

export const reasonLabel = (reason: string | null): string => (reason && REASON_LABELS[reason]) || reason || 'Unknown';

const MAX_CELL_LENGTH = 40;

// Keeps the start of a long value, so one long email cannot stretch the table. The whole value and
// its length are shown in the cell's tooltip instead.
export const shorten = (value: string): string =>
	value.length > MAX_CELL_LENGTH ? `${value.slice(0, MAX_CELL_LENGTH)}…` : value;

const characters = (value: string): string => `${value.length} characters`;

/** "ID 7 · PRO", the identifying line under a company name. Leaves out the tier when it is unknown. */
export const companyMeta = (companyId: number | null, tier: string | null): string =>
	tier ? `ID ${companyId} · ${tier}` : `ID ${companyId}`;

export interface BackfillGuestRow {
	id: string;
	personId: number | null;
	guestName: string;
	email: string;
	clientId: number | null;
	clientName: string;
	guestSince: string;
	reason: string;
	/** What hovering the Guest cell shows: the whole name and email. */
	guestTooltip: CellTooltip;
	/** What hovering the Client cell shows: the whole client name and its id. */
	clientTooltip: CellTooltip;
}

/**
 * A cell's hover tooltip. `note` gives the length of each value the cell had to shorten (for example
 * "Email: 190 characters"), and is null when the cell shows everything.
 */
export interface CellTooltip {
	title: string;
	details: string;
	note: string | null;
}

export interface BackfillCompanyGroup {
	id: string;
	companyId: number | null;
	companyName: string;
	meta: string;
	/**
	 * The server's count of contacts this company would create. It can be larger than `guests.length`:
	 * the server lists at most a fixed number of guests per company, but always counts every one.
	 */
	contactsToCreate: number;
	guests: BackfillGuestRow[];
}

const isShortened = (value: string): boolean => value.length > MAX_CELL_LENGTH;

const toGuestRow = (companyId: number | null, contact: BackfillContact): BackfillGuestRow => {
	const name = [contact.firstName, contact.lastName].filter(part => part && part.trim()).join(' ') || '(no name)';
	const email = contact.email || '(no email)';
	const clientName = contact.clientName || '';
	return {
		id: `${companyId}-${contact.personId}`,
		personId: contact.personId,
		guestName: shorten(name),
		email: shorten(email),
		clientId: contact.clientId,
		clientName: shorten(clientName),
		// The company service sends a SQL timestamp such as "2026-09-28 14:20:30.0"; the day is enough here.
		guestSince: (contact.personCreatedAt || '').slice(0, 10),
		reason: reasonLabel(contact.reason),
		guestTooltip: {
			title: name,
			details: email,
			note:
				[
					isShortened(name) ? `Name: ${characters(name)}` : null,
					isShortened(email) ? `Email: ${characters(email)}` : null,
				]
					.filter(Boolean)
					.join(' · ') || null,
		},
		clientTooltip: {
			title: clientName,
			details: `ID ${contact.clientId}`,
			note: isShortened(clientName) ? characters(clientName) : null,
		},
	};
};

/**
 * One group per company that has at least one contact to create, holding only the guests a real run
 * would insert. Skipped guests are left out; the page reports them as a count instead. Largest
 * company first, ties broken by company id so the order is stable between runs.
 */
export const toCompanyGroups = (companies: ReadonlyArray<BackfillCompany>): BackfillCompanyGroup[] =>
	companies
		.map(company => ({
			id: String(company.companyId),
			companyId: company.companyId,
			companyName: company.companyName || '',
			meta: companyMeta(company.companyId, company.tier),
			contactsToCreate: company.contactsToCreate || 0,
			guests: (company.contacts || [])
				.filter((c): c is BackfillContact => c != null && c.willCreate === true)
				.map(c => toGuestRow(company.companyId, c)),
		}))
		.filter(group => group.contactsToCreate > 0)
		.sort((a, b) => b.contactsToCreate - a.contactsToCreate || (a.companyId || 0) - (b.companyId || 0));

/** The no-back-fill banner's explanation, which depends on whether any company could not be checked. */
export const noBackfillNote = (failedCount: number): string =>
	failedCount > 0
		? `The dry run found 0 contacts to create. ${failedCount} ${failedCount === 1 ? 'company' : 'companies'} failed and ${
				failedCount === 1 ? 'was' : 'were'
		  } not checked — see below.`
		: 'The dry run found 0 contacts to create. Running the back-fill now would not change anything.';
